import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { lastValueFrom, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { TuiSheetOptions } from '@taiga-ui/legacy';
import { TUI_NUMBER_FORMAT } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';
import { TokenWithEmailService, TokenModel } from '@src/api';
import { SessionService, StorageService } from '@src/app/modules/auth';
import { TelegramWebAppService } from '@src/app/modules/telegram-web-app';
import { EnvService } from '@src/app/modules/env';
import { environment } from '@src/environments/environment';
import { AlertService } from '@src/core/services';
import { StorageKeys } from '@src/constants/storage';
import { AppConfigType } from '@src/app/modules/app-config';
import { BaseResizableComponent } from '@src/app/modules/base';

import { convertAppConfigToDownloadLinks, DownloadLinksComponentOptions } from './components/download-links';
import { EnterType, LoginEnterType } from './login.model';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: TUI_NUMBER_FORMAT,
      useValue: { thousandSeparator: '' },
    },
  ],
})
export class LoginComponent extends BaseResizableComponent {
  public readonly env = inject(EnvService);
  private readonly session = inject(SessionService);
  private readonly tokenService = inject(TokenWithEmailService);
  private readonly telegramWebAppService = inject(TelegramWebAppService);
  private readonly alertService = inject(AlertService);
  private readonly storageService = inject(StorageService);
  private readonly translateService = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);

  /** Конфигурация приложения */
  appConfig?: AppConfigType | null;

  /** Тип входа в приложение */
  enterType: LoginEnterType = this.env.isBot ? EnterType.Bot : EnterType.Email;

  /** Номер телефона */
  phoneNumber = '';

  /** Открыта политика конфиденциальности */
  openPolicy = false;

  /** Номер версии приложения */
  readonly APP_VERSION = environment.appVersion;

  /** Флаг отображения шторки скачивания приложения, если зашли под мобильной платформой */
  appSheetOpen = false;

  loading = false;

  readonly appSheetOptions: Partial<TuiSheetOptions> = {
    overlay: true,
  };

  downloadLinksOptions?: DownloadLinksComponentOptions;

  readonly EnterType = EnterType;

  override async ngOnInit(): Promise<void> {
    super.ngOnInit();

    this.storageService.setItem(StorageKeys.EnterType, EnterType.Email);

    if (this.session.appConfig) {
      this.appConfig = this.session.appConfig;

      this.downloadLinksOptions = this.appConfig ? convertAppConfigToDownloadLinks(this.appConfig) : undefined;
    }

    this.appSheetOpen =
      (this.env.isAndroidWithoutCordova && !!this.downloadLinksOptions?.androidDownloadLink) ||
      (this.env.isIosWithoutCordova && !!this.downloadLinksOptions?.iosDownloadLink);

    if (this.telegramWebAppService.isRunAsTelegramWebApp()) {
      // Работаем из под Telegram бота
      const token = await this.getTokenForTgWebApp();
      this.onApplyToken(token);
    } else {
      this.enterType = EnterType.Email;
    }

    this.route.queryParamMap
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(query => {
        this.openPolicy = query.get('page') === 'policy';
        this.cdr.markForCheck();
      })
      .unsubscribe();

    this.cdr.markForCheck();
  }

  async getTokenForTgWebApp(associationId?: string) {
    const initData = this.telegramWebAppService.getData();
    if (!initData) {
      return null;
    }

    // TODO: возможно нужно обращение к api вынести в TelegramWebAppService. тут оставить только подписку и обработку токена
    return lastValueFrom(
      this.tokenService.getTokenForTApp({ initData, associationId }).pipe(
        catchError(errorMessage => {
          this.alertService.error(errorMessage, {
            label: this.translateService.instant('components.login.alerts.errors.tokenInvalid'),
            autoClose: 0,
          });
          return throwError(() => errorMessage);
        }),
        takeUntil(this.destroyed$$),
      ),
    );
  }

  onApplyToken(token: TokenModel | null) {
    if (token) {
      this.session.createSession(token, true).then();
    }
  }

  onLoading(loading: boolean) {
    this.loading = loading;
    this.cdr.markForCheck();
  }
}
